import {
  fetchCurrentFeedbackSession,
  fetchFeedbackTours,
  openFeedbackSession
} from '@api/taptotip/questionnaire';
import TopNav from '@components/Nav/TopNav';
import TipDirectLeftMenu from '@components/Nav/TopNav/TipDirectLeftMenu';
import { StyleBreakpoints } from '@constants';
import { formatDayFirst } from '@utils/dayFirstDates';
import { makeStyles } from '@material-ui/core/styles';
import { formatRegionalLinks, isQuestionnairesEnabledForCompany } from '@utils/helpers';
import PropTypes from 'prop-types';
import { QRCodeCanvas } from 'qrcode.react';
import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import messages from './messages';

/**
 * Card 323 — the guide's guest feedback QR, from the web app.
 *
 * On the last day of a multi-day tour the guide (RIT: the Tour Manager) opens
 * this, says which tour and departure it is, and holds up the QR. Each guest
 * scans it and fills the form in on their own phone (TA-Tipping, card 322).
 *
 * The guide sees how many answers arrived in the last 48 hours and when, and
 * nothing else: no scores, no comments, no names. John's brief is that the
 * feedback goes to head office and not through the Tour Manager, and the API
 * does not return anything more even if asked.
 */

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',

    [theme.breakpoints.up(StyleBreakpoints.sm)]: {
      flexDirection: 'row'
    }
  }
}));

const CARD =
  'w-full bg-white rounded-3xl shadow-sm border border-solid border-slate-200 p-6 min-w-0';
const PRIMARY =
  'w-full sm:w-auto px-5 py-3 rounded-xl bg-indigo-600 text-white text-base font-semibold border-0 cursor-pointer disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed';
const QUIET =
  'px-4 py-3 rounded-xl bg-transparent text-slate-600 border-0 cursor-pointer underline';
const FIELD =
  'w-full h-12 px-3 rounded-xl border border-solid border-slate-300 bg-white text-base text-slate-900';

const REFRESH_MS = 30000;
const MAX_DEPARTURE_AGE_DAYS = 60;

/** YYYY-MM-DD in the guide's own timezone, which is what they mean by "today". */
export const localDate = (date = new Date()) => {
  const pad = n => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const daysAgo = days => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return localDate(date);
};

/**
 * Keep the phone's screen on while the QR is up. A guide walking a QR down a
 * coach aisle should not have to keep waking the phone. Best effort: not every
 * browser has the API, and the lock is dropped whenever the tab is hidden.
 */
const useWakeLock = active => {
  useEffect(() => {
    if (!active || typeof navigator === 'undefined' || !navigator.wakeLock) return undefined;
    let lock = null;
    let released = false;
    const acquire = () => {
      navigator.wakeLock
        .request('screen')
        .then(sentinel => {
          if (released) sentinel.release();
          else lock = sentinel;
        })
        .catch(() => {});
    };
    const onVisible = () => document.visibilityState === 'visible' && acquire();
    acquire();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      released = true;
      document.removeEventListener('visibilitychange', onVisible);
      if (lock) lock.release().catch(() => {});
    };
  }, [active]);
};

const GuestFeedback = ({ user }) => {
  const classes = useStyles();
  const intl = useIntl();

  const [status, setStatus] = useState('loading');
  const [tours, setTours] = useState([]);
  const [session, setSession] = useState(null);
  const [picking, setPicking] = useState(false);
  const [tourId, setTourId] = useState('');
  const [departureDate, setDepartureDate] = useState(localDate());
  const [opening, setOpening] = useState(false);
  const [openError, setOpenError] = useState(false);

  const enabled = isQuestionnairesEnabledForCompany(user);

  const load = useCallback(() => {
    setStatus('loading');
    Promise.all([fetchFeedbackTours(), fetchCurrentFeedbackSession()])
      .then(([choices, current]) => {
        const list = (choices && choices.tours) || [];
        setTours(list);
        setSession((current && current.session) || null);
        if (list.length === 1) setTourId(list[0].tourId);
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
  }, []);

  useEffect(() => {
    if (enabled) load();
  }, [enabled, load]);

  // The count under the QR keeps itself current while guests are scanning.
  useEffect(() => {
    if (!session || picking) return undefined;
    const timer = setInterval(() => {
      fetchCurrentFeedbackSession()
        .then(current => {
          if (current && current.session) setSession(current.session);
        })
        .catch(() => {});
    }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [session, picking]);

  const showingQr = status === 'ready' && session && !picking;
  useWakeLock(showingQr);

  const open = () => {
    setOpening(true);
    setOpenError(false);
    openFeedbackSession({ tourId, departureDate })
      .then(opened => {
        setSession(opened);
        setPicking(false);
      })
      .catch(() => setOpenError(true))
      .finally(() => setOpening(false));
  };

  const startPicking = () => {
    if (session) {
      setTourId(session.tourId);
      setDepartureDate(session.departureDate);
    }
    setOpenError(false);
    setPicking(true);
  };

  // Day-first in English ("20 September 2026"), QA 2026-10-20.
  const formatDeparture = date =>
    formatDayFirst(date, { day: 'numeric', month: 'long', year: 'numeric' }, intl.locale);

  const renderPicker = () => {
    const single = tours.length === 1;
    return (
      <div className={CARD} data-testid="feedback-picker">
        <h2 className="text-lg font-semibold text-slate-900 m-0 mb-2">
          {intl.formatMessage(messages.pickTitle)}
        </h2>
        <p className="text-slate-600 m-0 mb-5">{intl.formatMessage(messages.pickBody)}</p>

        <div className="flex flex-col gap-2 mb-5">
          <label htmlFor="feedback-tour" className="text-sm font-semibold text-slate-800">
            {intl.formatMessage(messages.tourLabel)}
          </label>
          {single ? (
            <p className="text-base text-slate-900 m-0" data-testid="feedback-single-tour">
              {tours[0].tourName}
            </p>
          ) : (
            <select
              id="feedback-tour"
              className={FIELD}
              value={tourId}
              onChange={event => setTourId(event.target.value)}
              data-testid="feedback-tour">
              <option value="">{intl.formatMessage(messages.tourPlaceholder)}</option>
              {tours.map(tour => (
                <option key={tour.tourId} value={tour.tourId}>
                  {tour.tourName}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="flex flex-col gap-2 mb-6">
          <label htmlFor="feedback-date" className="text-sm font-semibold text-slate-800">
            {intl.formatMessage(messages.dateLabel)}
          </label>
          <input
            id="feedback-date"
            type="date"
            className={FIELD}
            value={departureDate}
            min={daysAgo(MAX_DEPARTURE_AGE_DAYS)}
            max={localDate()}
            onChange={event => setDepartureDate(event.target.value)}
            aria-describedby="feedback-date-hint"
            data-testid="feedback-date"
          />
          <span id="feedback-date-hint" className="text-sm text-slate-500">
            {intl.formatMessage(messages.dateHint)}
          </span>
        </div>

        {openError && (
          <p className="text-sm text-red-700 m-0 mb-4" role="alert">
            {intl.formatMessage(messages.openFailed)}
          </p>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            className={PRIMARY}
            disabled={!tourId || !departureDate || opening}
            onClick={open}
            data-testid="feedback-open">
            {intl.formatMessage(messages.showQr)}
          </button>
          {session && (
            <button type="button" className={QUIET} onClick={() => setPicking(false)}>
              {intl.formatMessage(messages.cancel)}
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderQr = () => {
    const { recent } = session;
    return (
      <div className={`${CARD} flex flex-col items-center text-center`} data-testid="feedback-qr">
        <h2 className="text-xl font-semibold text-slate-900 m-0 mb-1">{session.tourName}</h2>
        <p className="text-slate-600 m-0 mb-5">
          {intl.formatMessage(messages.departed, { date: formatDeparture(session.departureDate) })}
        </p>

        {/* Big enough to scan from arm's length down a coach aisle, and never
            wider than the phone showing it. */}
        <div className="w-full max-w-[320px] p-3 bg-white rounded-2xl border border-solid border-slate-200">
          <QRCodeCanvas
            value={formatRegionalLinks(session.path)}
            size={296}
            includeMargin
            style={{ width: '100%', height: 'auto' }}
          />
        </div>

        <h3 className="text-lg font-semibold text-slate-900 m-0 mt-6 mb-1">
          {intl.formatMessage(messages.qrTitle)}
        </h3>
        <p className="text-slate-600 m-0 mb-6 max-w-[34ch]">
          {intl.formatMessage(messages.qrBody)}
        </p>

        <div className="w-full rounded-2xl bg-slate-50 p-4 mb-4" data-testid="feedback-recent">
          <p className="text-base font-semibold text-slate-900 m-0" aria-live="polite">
            {recent.count > 0
              ? intl.formatMessage(messages.recentCount, {
                  count: recent.count,
                  hours: recent.hours
                })
              : intl.formatMessage(messages.recentNone, { hours: recent.hours })}
          </p>
          {recent.count > 0 && (
            <p className="text-sm text-slate-600 m-0 mt-1" data-testid="feedback-times">
              {recent.times
                .slice(0, 12)
                .map(time => intl.formatTime(new Date(time), { hour: 'numeric', minute: '2-digit' }))
                .join(', ')}
            </p>
          )}
          <p className="text-sm text-slate-500 m-0 mt-2">
            {intl.formatMessage(messages.recentPrivacy)}
          </p>
        </div>

        <button
          type="button"
          className={QUIET}
          onClick={startPicking}
          data-testid="feedback-change">
          {intl.formatMessage(messages.changeDeparture)}
        </button>
      </div>
    );
  };

  const renderBody = () => {
    if (status === 'loading') {
      return (
        <div className={CARD} role="status" aria-label={intl.formatMessage(messages.loading)}>
          <div className="mx-auto aspect-square w-full max-w-[320px] bg-slate-100 rounded-2xl" />
          <div className="h-4 bg-slate-100 rounded mt-6 w-2/3 mx-auto" />
        </div>
      );
    }

    if (status === 'error') {
      return (
        <div className={CARD}>
          <p className="text-slate-600 m-0 mb-4">{intl.formatMessage(messages.loadFailed)}</p>
          <button
            type="button"
            onClick={load}
            className="px-4 py-2 rounded-xl bg-slate-900 text-white border-0 cursor-pointer">
            {intl.formatMessage(messages.retry)}
          </button>
        </div>
      );
    }

    if (tours.length === 0 && !session) {
      return (
        <div className={CARD} data-testid="feedback-no-tours">
          <h2 className="text-lg font-semibold text-slate-900 m-0 mb-2">
            {intl.formatMessage(messages.noToursTitle)}
          </h2>
          <p className="text-slate-600 m-0">{intl.formatMessage(messages.noToursBody)}</p>
        </div>
      );
    }

    return session && !picking ? renderQr() : renderPicker();
  };

  // The nav hides the tab until the company is switched on, but the route can
  // be typed. Same guard as Tour Photos (card 305), after every hook.
  if (!enabled) {
    return <Redirect to="/" />;
  }

  return (
    <>
      <TopNav />
      <div className={classes.container}>
        <TipDirectLeftMenu user={user} selectedTab="Feedback" />
        <div className="flex flex-col gap-6 w-full max-w-2xl p-4 sm:p-8 min-w-0">
          <h1 className="text-2xl font-bold text-slate-900 m-0">
            {intl.formatMessage(messages.title)}
          </h1>
          {renderBody()}
        </div>
      </div>
    </>
  );
};

GuestFeedback.propTypes = {
  user: PropTypes.object
};

// Same shape the Dashboard and Tour Photos use.
const mapStateToProps = state => ({
  user: state.user?.user
});

export { GuestFeedback };
export default connect(mapStateToProps)(GuestFeedback);
